import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { InputNumber } from 'primereact/inputnumber';
import reportsServices from "../../services/reports/reportservice";

interface DataTableProps {
  data: any[];
  selectedMonth?: number;
  selectedYear?: number;
}

const POSTargetMappingListDataTable: React.FC<DataTableProps> = ({ data, selectedMonth, selectedYear }) => {
  const toast = useRef<Toast>(null);
  const [orderDataList, setOrderDataList] = useState<any>([]);
  const [totalTarget, setTotalTarget] = useState<number>(0);

  // Calculate if the selected month/year is in the past
  const [isPastMonth, setIsPastMonth] = useState<boolean>(false);

  useEffect(() => {
    if (selectedMonth && selectedYear) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1; // 1-12

      // It's a past month if the year is in the past, or same year but past month
      if (
        selectedYear < currentYear ||
        (Number(selectedYear) === currentYear && selectedMonth < currentMonth)
      ) {
        setIsPastMonth(true);
      } else {
        setIsPastMonth(false);
      }
    }
  }, [selectedMonth, selectedYear]);

  // Calculate totals whenever data changes
  useEffect(() => {
    setOrderDataList(data);

    const total = data?.reduce((sum, row) => {
      return sum + (parseFloat(row.target) || 0);
    }, 0);

    setTotalTarget(total);
  }, [data]);

  const textEditor = (options: any) => {
    return (
      <InputNumber
        value={options.value}
        onChange={(e: any) => options.editorCallback(e.value, options.index)}
        mode="currency"
        currency="INR"
        locale="en-US"
        min={1}
      />
    );
  };

  const onRowEditComplete = (e: any) => {
    let _products = [...orderDataList];
    let { newData, index } = e;

    if (!newData.target || newData.target <= 0) {
      toast.current?.show({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Target must be greater than 0.',
        life: 3000
      });
      return;
    }

    _products[index] = newData;

    reportsServices.updatePOSTargetMapping(newData).then((response: any) => {
      if (response.isSuccess) {
        _products[index].id = response.id;
        setOrderDataList(_products);
        
        // Update total after edit
        const newTotal = _products.reduce((sum, row) => {
          return sum + (parseFloat(row.target) || 0);
        }, 0);
        setTotalTarget(newTotal);

        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: response.message,
          life: 3000
        });
      }
    });
  };

  const formatCurrency = (value: any) => {
    return value.toLocaleString('en-US', { style: 'currency', currency: 'INR' });
  };

  const balanceBodyTemplate = (rowData: any) => {
    return formatCurrency(rowData.target);
  };

  return (
    <>
      <Toast ref={toast} />
      <div className="col-12 card border-noround p-2">
        {orderDataList && (
          <DataTable
            editMode="row"
            onRowEditComplete={onRowEditComplete}
            value={orderDataList}
            paginator
            rows={10}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            stripedRows
            showGridlines
            emptyMessage="No order found."
            paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
            currentPageReportTemplate="{first} to {last} of {totalRecords}"
          >
            <Column
              field="posName"
              header="Name"
              style={{ width: '20%' }}
              footer="Total:"
            ></Column>

            <Column
              field="target"
              header="Target"
              body={balanceBodyTemplate}
              editor={(options) => textEditor(options)}
              style={{ width: '20%' }}
              footer={formatCurrency(totalTarget)}
            ></Column>

            {!isPastMonth && (
                <Column
                  rowEditor={true}
                  header="Action"
                  headerStyle={{ width: '10%', minWidth: '8rem' }}
                  bodyStyle={{ textAlign: 'center' }}
                ></Column>
            )}
          </DataTable>
        )}
      </div>
    </>
  );
};

export default POSTargetMappingListDataTable;
